import React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { reduxForm, FieldArray, Field } from 'redux-form';
import { Button, Form, Modal, Grid, Message } from 'semantic-ui-react';
import { v4 as uuidV4 } from 'uuid';

// Global Types
import {
    HarvestEventForecastedQuantity,
    HarvestEventsProps,
    HarvestEventUuid
} from 'services/harvestEvents/types';

// Global Actions
import { updateHarvestEvent } from 'services/harvestEvents/actions';
import { updateQueryString } from 'services/router/actions';

// Global Selectors
import { makeSelectHarvestEventForecastedQuantity } from 'services/harvestEvents/selectors';

// Components
// @ts-ignore
import UpdateGroupValueOnNewGroupCreation from 'components/UpdateGroupValueOnNewGroupCreation';
// @ts-ignore
import CreateGroupButton from 'components/CreateGroupButton';
import { EntityId } from 'services/entities/types';
import AssignDefaultStaffMembersLink from 'components/AssignDefaultStaffMembersLink';
import PickTypeNameValue from '../../../../../../components/PickTypeNameValue';
// @ts-ignore
import EntityHierarchyReversed from '../../../../../../components/EntityHierarchyReversed';
// @ts-ignore
import SubmittingButton from '../../../../../../components/SubmittingButton';
// @ts-ignore
import GroupDropdownFilterDropdown from '../../../../../../components/GroupDropdownFilterDropdown';
// @ts-ignore
import GroupOptions from '../../../../../../components/GroupOptions';
// @ts-ignore
import UsersTagInput from '../../../../../../components/UsersTagInput';
// @ts-ignore
import DefaultFieldValue from '../../../../../../components/DefaultFieldValue';
// @ts-ignore
import RenderQuantitySchedules from './components/RenderQuantitySchedules';
// @ts-ignore
import ScheduledAndForecastedTotals from './components/ScheduledAndForecastedTotals';

// Selectors
// @ts-ignore
import { selectCurrentSeasonIsComplete } from '../../../../../../services/currentSeasonId/selectors';
// @ts-ignore
import { selectInitialValues, makeSelectFormValue } from './selectors';

// Form
// @ts-ignore
import { normalizers, RenderDropdown } from '../../../../../../forms';

// Constants
// @ts-ignore
import { FORM_NAME } from './constants';

// Normalizers
const { toArrayIfFalsy } = normalizers;

const NAMESPACE = ['EVENT_CREATE', 'EVENT_UPDATE'];

interface OwnProps extends HarvestEventsProps {
    onClose: Function;
    entityId?: EntityId;
    harvestEventUuid?: HarvestEventUuid;
    defaultValues?: any; // CHANGE THIS
}

interface ConnectedProps {
    initialValues: Record<string, any>;
    pickTypeId: number;
    forecastedQuantity: HarvestEventForecastedQuantity;
    isDifferentFromForecast?: boolean;
    disabled?: boolean;
}

interface DispatchProps {
    updateHarvestEvent: Function;
    updateQueryString: typeof updateQueryString;
}

interface FormProps {
    error?: string;
    handleSubmit: Function;
}

type Props = OwnProps & ConnectedProps & DispatchProps & FormProps;

class SeasonalJobForm extends React.PureComponent<Props> {
    handleSubmit = (values: Record<string, any>): void => {
        const { harvestEventUuid, onClose, ...props } = this.props;

        const uuid = harvestEventUuid || uuidV4();

        props.updateHarvestEvent({
            ...values,
            uuid,
            jobs: values.jobs.map((value: any) => ({
                ...value,
                assignedUsers: value.assignedUsers
                    ? value.assignedUsers.map((assignedUser: string) => ({
                          email: assignedUser
                      }))
                    : []
            }))
        });

        props.updateQueryString({
            selectedEvent: uuid,
            seasonalEventModal: 'updated',
            selectedCurrentEntityId: undefined,
            newGroup: undefined,
            seasonalEventDefaultValues: undefined
        });
    };

    render(): React.ReactElement {
        const {
            harvestEventUuid,
            pickTypeId,
            forecastedQuantity,
            isDifferentFromForecast,
            disabled,
            error,
            handleSubmit,
            onClose,
            initialValues,
            // @ts-ignore
            change
        } = this.props;
        const { entityId } = initialValues;

        return (
            <React.Fragment>
                <Modal.Header>
                    <PickTypeNameValue pickTypeId={pickTypeId} />
                    {' / '}
                    <EntityHierarchyReversed entityId={entityId} />
                </Modal.Header>
                <Modal.Content>
                    <Form
                        onSubmit={handleSubmit(this.handleSubmit)}
                        autoComplete="off"
                    >
                        <UpdateGroupValueOnNewGroupCreation
                            formName={FORM_NAME}
                            name="groupId"
                        />
                        <GroupOptions>
                            {(options: any): React.ReactElement => (
                                <Field
                                    component={RenderDropdown}
                                    name="groupIds"
                                    label="Groups (optional)"
                                    placeholder="Add group"
                                    normalize={toArrayIfFalsy}
                                    disabled={disabled}
                                    options={options}
                                    clearable={!disabled}
                                    fluid
                                    search
                                    selection
                                    selectOnBlur={false}
                                    multiple
                                    renderOptions={
                                        !disabled && (
                                            <GroupDropdownFilterDropdown
                                                resetOnNewGroupCreation
                                            />
                                        )
                                    }
                                />
                            )}
                        </GroupOptions>
                        {!disabled && <CreateGroupButton />}
                        <DefaultFieldValue
                            formName={FORM_NAME}
                            name="jobs[0].jobType"
                            value="INSPECTION"
                        />
                        <div className="field-with-link-button">
                            <UsersTagInput
                                label="QA (optional)"
                                placeholder="Add staff member"
                                name="jobs[0].assignedUsers"
                                disabled={disabled}
                            />
                            <AssignDefaultStaffMembersLink
                                entityId={entityId as number}
                                fieldName="jobs[0].assignedUsers"
                                change={change}
                            />
                        </div>
                        <DefaultFieldValue
                            formName={FORM_NAME}
                            name="jobs[1].jobType"
                            value="TIMESHEET"
                        />
                        <div className="field-with-link-button">
                            <UsersTagInput
                                label="Bin check (optional)"
                                placeholder="Add staff member"
                                name="jobs[1].assignedUsers"
                                disabled={disabled}
                            />
                            <AssignDefaultStaffMembersLink
                                entityId={entityId as number}
                                fieldName="jobs[1].assignedUsers"
                                change={change}
                            />
                        </div>
                        <FieldArray
                            name="quantitySchedules"
                            component={RenderQuantitySchedules}
                            forecastedQuantity={forecastedQuantity}
                            isDifferentFromForecast={isDifferentFromForecast}
                            rerenderOnEveryChange
                            disabled={disabled}
                        />
                        <ScheduledAndForecastedTotals
                            harvestEventUuid={harvestEventUuid}
                        />
                        {error && <Message negative>{error}</Message>}
                        <Grid>
                            <Grid.Column width={8}>
                                <Button
                                    type="button"
                                    onClick={(): void => onClose()}
                                    fluid
                                    basic
                                    primary
                                >
                                    Cancel
                                </Button>
                            </Grid.Column>
                            <Grid.Column width={8}>
                                <SubmittingButton
                                    type="submit"
                                    namespace={NAMESPACE}
                                    primary
                                    disabled={disabled}
                                    fluid
                                >
                                    Submit
                                </SubmittingButton>
                            </Grid.Column>
                        </Grid>
                    </Form>
                </Modal.Content>
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector<any, any, any>({
    initialValues: selectInitialValues,
    pickTypeId: makeSelectFormValue('harvestEvent.pickTypeId'),
    forecastedQuantity: makeSelectHarvestEventForecastedQuantity(),
    isDifferentFromForecast: makeSelectFormValue('isDifferentFromForecast'),
    disabled: selectCurrentSeasonIsComplete
});

export default connect(mapStateToProps, {
    updateHarvestEvent,
    updateQueryString
})(
    reduxForm<any, any>({
        form: FORM_NAME
    })(SeasonalJobForm)
);
